import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Bookmark as BookmarkIcon,
  ChevronRight as ChevronRightIcon,
  NotificationsActive as NotificationsActiveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';

/**
 * Row for the 4 "Окремі набори" categories (books / practical / professional /
 * community). All of them are coming-soon today — the row renders with a
 * dashed border, a "Скоро" eyebrow badge and a "Сповістити" CTA in place of
 * the usual "Відкрити →".
 *
 * Three things differ from {@link WordSetRow}:
 *  • Thumbnail is an icon tile with a gradient tinted by the category accent
 *    (vs. the curriculum's illustrated PNGs).
 *  • Content includes a type-specific preview cluster (book spines / tag
 *    chips / community featured cards) above the stat pills.
 *  • The row is rendered as a static container (no Link) when `soon` —
 *    nothing exists behind it to navigate to yet.
 */

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

type PreviewKind = 'books' | 'situations' | 'fields' | 'community';

export interface FeaturedSet {
  title: string;
  author: string;
  words: number;
  /** Pre-formatted save count, e.g. '4.2k' — kept as a string because the
   * data lives outside the system today. */
  saves: string;
}

export interface SourceRowProps {
  id: string;
  /** English uppercase eyebrow line. */
  en: string;
  /** Ukrainian title. */
  title: string;
  desc: string;
  /** Accent hex (#RRGGBB). Drives thumb gradient, eyebrow color, hover. */
  accent: string;
  /** MUI icon component for the thumbnail tile. */
  Icon: typeof BookmarkIcon;
  /** Which preview cluster renders between description and stats. */
  kind: PreviewKind;
  /** Stat pill payload (number + unit). */
  stats: Array<{ n: string; u: string }>;
  /** Coming-soon metadata. When undefined the row is rendered as an
   * interactive (link-capable) tile — left for future use. */
  soon?: boolean;
  /** Free-text ETA shown next to the Soon badge, e.g. 'Липень 2026'. */
  eta?: string;
  /** Data for `kind === 'books'` — a few book titles to render as spines. */
  books?: string[];
  /** Data for `kind === 'situations'` — tag chips. */
  situations?: string[];
  /** Data for `kind === 'fields'` — tag chips. Same chip style as situations
   * but a separate slot so the prop intent stays readable at the call site. */
  fields?: string[];
  /** Data for `kind === 'community'` — featured user sets. */
  featured?: FeaturedSet[];
}

// ── Sub-components ──────────────────────────────────────────────────────────

const StatPill: React.FC<{ n: string; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

const SoonBadge: React.FC<{ accent: string; eta?: string }> = ({
  accent,
  eta,
}) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: hexToRgba(accent, 0.14),
      borderRadius: 999,
      color: accent,
      display: 'inline-flex',
      fontSize: 11.5,
      fontWeight: 800,
      gap: 0.5,
      letterSpacing: '0.02em',
      px: 1.25,
      py: 0.375,
      whiteSpace: 'nowrap',
    }}
  >
    <ScheduleIcon sx={{ fontSize: 14 }} />
    {`Скоро${eta ? ` · ${eta}` : ''}`}
  </Box>
);

const NotifyButton: React.FC<{ accent: string }> = ({ accent }) => (
  <Box
    component={'button'}
    type={'button'}
    onClick={(e) => e.preventDefault()}
    sx={{
      alignItems: 'center',
      bgcolor: 'transparent',
      border: '1px solid',
      borderColor: hexToRgba(accent, 0.45),
      borderRadius: 999,
      color: accent,
      cursor: 'pointer',
      display: 'inline-flex',
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 700,
      gap: 0.75,
      px: 1.75,
      py: 0.875,
      transition: 'all .15s ease',
      whiteSpace: 'nowrap',
      '&:hover': {
        bgcolor: hexToRgba(accent, 0.06),
        borderColor: hexToRgba(accent, 0.8),
      },
    }}
  >
    <NotificationsActiveIcon sx={{ fontSize: 18 }} />
    {'Сповістити'}
  </Box>
);

const BookSpines: React.FC<{ accent: string; books: string[] }> = ({
  accent,
  books,
}) => (
  <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
    {books.map((b) => (
      <Box
        key={b}
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0.75,
          color: 'text.primary',
          display: 'inline-flex',
          fontSize: 13,
          fontWeight: 600,
          gap: 0.625,
          pl: 1,
          pr: 1.4,
          py: 0.625,
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          sx={{
            bgcolor: accent,
            borderRadius: 0.25,
            flex: 'none',
            height: 16,
            width: 12,
          }}
        />
        {b}
      </Box>
    ))}
    <Box
      sx={{
        alignItems: 'center',
        color: accent,
        display: 'inline-flex',
        fontSize: 13,
        fontWeight: 700,
        px: 1.4,
        py: 0.625,
      }}
    >
      {'+ ще 4'}
    </Box>
  </Stack>
);

const TagChips: React.FC<{
  accent: string;
  items: string[];
  max?: number;
}> = ({ accent, items, max }) => {
  const list = max ? items.slice(0, max) : items;
  return (
    <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {list.map((s) => (
        <Box
          key={s}
          sx={{
            bgcolor: hexToRgba(accent, 0.06),
            border: '1px solid',
            borderColor: hexToRgba(accent, 0.35),
            borderRadius: 999,
            color: 'text.primary',
            fontSize: 13.5,
            px: 1.5,
            py: 0.625,
            whiteSpace: 'nowrap',
          }}
        >
          {s}
        </Box>
      ))}
    </Stack>
  );
};

const CommunityMini: React.FC<{
  accent: string;
  featured: FeaturedSet[];
}> = ({ accent, featured }) => (
  <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1.25 }}>
    {featured.map((f) => (
      <Box
        key={f.title}
        sx={{
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          display: 'flex',
          flex: '1 1 200px',
          flexDirection: 'column',
          gap: 0.75,
          minWidth: 0,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {f.title}
        </Typography>
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={1}
          sx={{ color: 'text.secondary', fontSize: 12 }}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: hexToRgba(accent, 0.18),
                borderRadius: '50%',
                color: accent,
                display: 'inline-flex',
                fontSize: 10,
                fontWeight: 800,
                height: 18,
                justifyContent: 'center',
                width: 18,
              }}
            >
              {f.author[0]}
            </Box>
            <Box component={'span'}>{f.author}</Box>
          </Stack>
          <Box component={'span'}>·</Box>
          <Box component={'span'}>{`${f.words} слів`}</Box>
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={0.375}
            sx={{
              color: accent,
              fontWeight: 700,
              ml: 'auto',
            }}
          >
            <BookmarkIcon sx={{ fontSize: 14 }} />
            {f.saves}
          </Stack>
        </Stack>
      </Box>
    ))}
  </Stack>
);

// ── Main row ────────────────────────────────────────────────────────────────

const PREVIEW_CTA: Record<PreviewKind, string> = {
  books: 'Усі книги',
  situations: 'Усі ситуації',
  fields: 'Усі галузі',
  community: 'Переглянути',
};

const SourceRow: React.FC<SourceRowProps> = ({
  en,
  title,
  desc,
  accent,
  Icon,
  kind,
  stats,
  soon,
  eta,
  books,
  situations,
  fields,
  featured,
}) => {
  const cta = PREVIEW_CTA[kind];
  return (
    <UIBox
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        borderStyle: soon ? 'dashed' : 'solid',
        cursor: soon ? 'default' : 'pointer',
        overflow: 'hidden',
        // Hover only when active (not soon) — same accent treatment as
        // WordSetRow so the two row families read as a coherent catalog.
        transition: 'border-color .16s ease, background-color .16s ease',
        ...(soon
          ? {}
          : {
              '&:hover': {
                bgcolor: hexToRgba(accent, 0.04),
                borderColor: accent,
              },
            }),
      }}
    >
      <Stack
        direction={'row'}
        sx={{
          alignItems: { xs: 'flex-start', md: 'stretch' },
          gap: { xs: 1.75, md: 2.5 },
          p: { xs: '14px', md: '18px 20px' },
        }}
      >
        {/* Icon thumbnail */}
        <Box
          sx={{
            alignItems: 'center',
            background: `linear-gradient(150deg, ${hexToRgba(
              accent,
              0.16,
            )}, ${hexToRgba(accent, 0.06)})`,
            border: '1px solid',
            borderColor: hexToRgba(accent, 0.22),
            borderRadius: 1.5,
            display: 'flex',
            flex: 'none',
            height: { xs: 60, md: 96 },
            justifyContent: 'center',
            opacity: soon ? 0.55 : 1,
            width: { xs: 60, md: 96 },
          }}
        >
          <Icon sx={{ color: accent, fontSize: { xs: 28, md: 40 } }} />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header — eyebrow + soon badge + (desktop) right-aligned CTA */}
          <Stack
            direction={'row'}
            alignItems={'flex-start'}
            justifyContent={'space-between'}
            spacing={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <UIText
                sx={{
                  color: 'text.primary',
                  fontSize: { xs: 10, md: 11 },
                  fontWeight: 800,
                  letterSpacing: '0.09em',
                  mb: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                {en}
              </UIText>
              <UIText
                variant={'h3'}
                sx={{ color: accent, fontSize: { xs: 17, md: 21 }, mb: 0.625 }}
              >
                {title}
              </UIText>
            </Box>
            {/* Desktop right cluster — Soon badge sits inline next to the
                Notify CTA so the "coming on X" hint is paired with the
                action, not split between the eyebrow and the button. */}
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={1}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flex: 'none',
              }}
            >
              {soon && <SoonBadge accent={accent} eta={eta} />}
              {soon ? (
                <NotifyButton accent={accent} />
              ) : (
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={0.5}
                  sx={{ color: accent, fontSize: 15, fontWeight: 600 }}
                >
                  {cta}
                  <ArrowForwardIcon sx={{ fontSize: 20 }} />
                </Stack>
              )}
            </Stack>
          </Stack>

          <UIText
            color={'primary.subtitle'}
            sx={{
              fontSize: { xs: 13, md: 14 },
              lineHeight: 1.45,
              maxWidth: 620,
              mb: 1.5,
            }}
          >
            {desc}
          </UIText>

          {/* Type-specific preview cluster */}
          <Box sx={{ mb: 1.5, opacity: soon ? 0.72 : 1 }}>
            {kind === 'books' && books && (
              <BookSpines accent={accent} books={books} />
            )}
            {kind === 'situations' && situations && (
              <TagChips accent={accent} items={situations} max={8} />
            )}
            {kind === 'fields' && fields && (
              <TagChips accent={accent} items={fields} max={8} />
            )}
            {kind === 'community' && featured && (
              <CommunityMini accent={accent} featured={featured} />
            )}
          </Box>

          {/* Stat pills + (planned) ETA echo */}
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.5}
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {stats.map((s, i) => (
                <StatPill key={i} n={s.n} u={s.u} />
              ))}
            </Stack>
          </Stack>

          {/* Mobile bottom CTA — same inline Soon-next-to-CTA pairing. */}
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1}
            sx={{ display: { xs: 'flex', md: 'none' }, flexWrap: 'wrap', mt: 1.5, rowGap: 0.75 }}
          >
            {soon && <SoonBadge accent={accent} eta={eta} />}
            {soon ? (
              <NotifyButton accent={accent} />
            ) : (
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={0.5}
                sx={{ color: accent, fontSize: 15, fontWeight: 600 }}
              >
                {cta}
                <ChevronRightIcon sx={{ fontSize: 22 }} />
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </UIBox>
  );
};

export default SourceRow;
